import React, { useState, useEffect, useCallback } from 'react';
import { BaseConnectorMapping, type MappingSection } from './BaseConnectorMapping';
import type { BaseMappingProps } from './index';
import type { SettingspageTranslations } from '@/hooks/useSettingspageTranslations';
import { useBigCommerceTranslations } from '@/hooks/useBigCommerceTranslations';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import api from '@/services/api';

interface BigCommerceMappingProps extends BaseMappingProps {
  i18n: SettingspageTranslations;
  configId?: number;
}

interface BigCommerceChannel {
  id: number;
  name: string;
  type: string;
  platform: string;
  status: string;
  is_enabled: boolean;
}

interface SalesChannel {
  id: number;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

function getBigCommerceMappingSections(
  bcTranslations: ReturnType<typeof useBigCommerceTranslations>
): MappingSection[] {
  return [
    {
      title: bcTranslations.mappingFields.sections.basicInfo.title,
      description: bcTranslations.mappingFields.sections.basicInfo.description,
      fields: [
        {
          key: 'description_attribute',
          label: bcTranslations.mappingFields.labels.productDescription,
          type: 'attribute',
          filterType: ['text', 'rich_text'],
          allowDefault: true,
        },
        {
          key: 'type_attribute',
          label: bcTranslations.mappingFields.labels.productType,
          type: 'attribute',
          filterType: ['select'],
          allowDefault: true,
        },
        {
          key: 'search_keywords_attribute',
          label: bcTranslations.mappingFields.labels.searchKeywords,
          type: 'attribute',
          filterType: ['text'],
          allowNone: true,
        },
        {
          key: 'condition_attribute',
          label: bcTranslations.mappingFields.labels.productCondition,
          type: 'attribute',
          filterType: ['select'],
          description: bcTranslations.mappingFields.fields.conditionValues,
          allowDefault: true,
        },
        {
          key: 'brand_id_attribute',
          label: bcTranslations.mappingFields.labels.brandId,
          type: 'attribute',
          filterType: ['number', 'text'],
          description: bcTranslations.mappingFields.fields.brandId,
          allowNone: true,
        },
      ],
    },
    {
      title: bcTranslations.mappingFields.sections.dimensions.title,
      description: bcTranslations.mappingFields.sections.dimensions.description,
      fields: [
        {
          key: 'weight_attribute',
          label: bcTranslations.mappingFields.labels.productWeight,
          type: 'attribute',
          filterType: ['measurement', 'number'],
          description: bcTranslations.mappingFields.fields.weightUnit,
          allowDefault: true,
        },
        {
          key: 'width_attribute',
          label: bcTranslations.mappingFields.labels.width,
          type: 'attribute',
          filterType: ['measurement', 'number'],
          allowNone: true,
        },
        {
          key: 'depth_attribute',
          label: bcTranslations.mappingFields.labels.depth,
          type: 'attribute',
          filterType: ['measurement', 'number'],
          allowNone: true,
        },
        {
          key: 'height_attribute',
          label: bcTranslations.mappingFields.labels.height,
          type: 'attribute',
          filterType: ['measurement', 'number'],
          allowNone: true,
        },
      ],
    },
    {
      title: bcTranslations.mappingFields.sections.pricing.title,
      description: bcTranslations.mappingFields.sections.pricing.description,
      fields: [
        {
          key: 'price_type_mapping',
          label: bcTranslations.mappingFields.labels.basePriceType,
          type: 'price_type',
          description: bcTranslations.mappingFields.fields.priceType,
          allowDefault: true,
        },
        {
          key: 'cost_price_attribute',
          label: bcTranslations.mappingFields.labels.costPrice,
          type: 'attribute',
          filterType: ['number'],
          allowNone: true,
        },
        {
          key: 'retail_price_attribute',
          label: bcTranslations.mappingFields.labels.retailPrice,
          type: 'attribute',
          filterType: ['number'],
          allowNone: true,
        },
        {
          key: 'sale_price_attribute',
          label: bcTranslations.mappingFields.labels.salePrice,
          type: 'attribute',
          filterType: ['number'],
          allowNone: true,
        },
        {
          key: 'map_price_attribute',
          label: bcTranslations.mappingFields.labels.mapPrice,
          type: 'attribute',
          filterType: ['number'],
          description: bcTranslations.mappingFields.fields.mapPrice,
          allowNone: true,
        },
      ],
    },
    {
      title: bcTranslations.mappingFields.sections.inventory.title,
      description: bcTranslations.mappingFields.sections.inventory.description,
      fields: [
        {
          key: 'inventory_level_attribute',
          label: bcTranslations.mappingFields.labels.stockQuantity,
          type: 'attribute',
          filterType: ['number'],
          allowNone: true,
        },
        {
          key: 'inventory_warning_level_attribute',
          label: bcTranslations.mappingFields.labels.lowStockWarningLevel,
          type: 'attribute',
          filterType: ['number'],
          allowDefault: true,
        },
        {
          key: 'inventory_tracking_attribute',
          label: bcTranslations.mappingFields.labels.trackInventory,
          type: 'attribute',
          filterType: ['boolean', 'select'],
          description: bcTranslations.mappingFields.fields.inventoryTracking,
          allowDefault: true,
        },
        {
          key: 'bin_picking_number_attribute',
          label: bcTranslations.mappingFields.labels.binPickingNumber,
          type: 'attribute',
          filterType: ['text'],
          allowNone: true,
        },
      ],
    },
    {
      title: bcTranslations.mappingFields.sections.shipping.title,
      description: bcTranslations.mappingFields.sections.shipping.description,
      fields: [
        {
          key: 'fixed_cost_shipping_price_attribute',
          label: bcTranslations.mappingFields.labels.fixedShippingCost,
          type: 'attribute',
          filterType: ['number'],
          allowNone: true,
        },
        {
          key: 'is_free_shipping_attribute',
          label: bcTranslations.mappingFields.labels.freeShipping,
          type: 'attribute',
          filterType: ['boolean'],
          allowDefault: true,
        },
      ],
    },
    {
      title: bcTranslations.mappingFields.sections.identifiers.title,
      description: bcTranslations.mappingFields.sections.identifiers.description,
      fields: [
        {
          key: 'upc_attribute',
          label: bcTranslations.mappingFields.labels.upcCode,
          type: 'attribute',
          filterType: ['text', 'number'],
          allowNone: true,
        },
        {
          key: 'gtin_attribute',
          label: bcTranslations.mappingFields.labels.gtin,
          type: 'attribute',
          filterType: ['text', 'number'],
          allowNone: true,
        },
        {
          key: 'mpn_attribute',
          label: bcTranslations.mappingFields.labels.mpn,
          type: 'attribute',
          filterType: ['text', 'number'],
          allowNone: true,
        },
        {
          key: 'isbn_attribute',
          label: bcTranslations.mappingFields.labels.isbn,
          type: 'attribute',
          filterType: ['text', 'number'],
          allowNone: true,
        },
        {
          key: 'ean_attribute',
          label: bcTranslations.mappingFields.labels.ean,
          type: 'attribute',
          filterType: ['text', 'number'],
          allowNone: true,
        },
      ],
    },
    {
      title: bcTranslations.mappingFields.sections.variants.title,
      description: bcTranslations.mappingFields.sections.variants.description,
      fields: [
        {
          key: 'option1_name',
          label: bcTranslations.mappingFields.labels.option1Name,
          type: 'text',
          description: bcTranslations.mappingFields.fields.optionExample,
          allowNone: true,
        },
        {
          key: 'option1_attribute',
          label: bcTranslations.mappingFields.labels.option1Attribute,
          type: 'attribute',
          filterType: ['select', 'text'],
          description: bcTranslations.mappingFields.fields.optionAttribute1,
          allowNone: true,
        },
        {
          key: 'option2_name',
          label: bcTranslations.mappingFields.labels.option2Name,
          type: 'text',
          description: bcTranslations.mappingFields.fields.optionExample,
          allowNone: true,
        },
        {
          key: 'option2_attribute',
          label: bcTranslations.mappingFields.labels.option2Attribute,
          type: 'attribute',
          filterType: ['select', 'text'],
          description: bcTranslations.mappingFields.fields.optionAttribute2,
          allowNone: true,
        },
        {
          key: 'option3_name',
          label: bcTranslations.mappingFields.labels.option3Name,
          type: 'text',
          description: bcTranslations.mappingFields.fields.optionExample,
          allowNone: true,
        },
        {
          key: 'option3_attribute',
          label: bcTranslations.mappingFields.labels.option3Attribute,
          type: 'attribute',
          filterType: ['select', 'text'],
          description: bcTranslations.mappingFields.fields.optionAttribute3,
          allowNone: true,
        },
      ],
    },
    {
      title: bcTranslations.mappingFields.sections.seo.title,
      description: bcTranslations.mappingFields.sections.seo.description,
      fields: [
        {
          key: 'meta_title_attribute',
          label: bcTranslations.mappingFields.labels.metaTitle,
          type: 'attribute',
          filterType: ['text'],
          allowNone: true,
        },
        {
          key: 'meta_description_attribute',
          label: bcTranslations.mappingFields.labels.metaDescription,
          type: 'attribute',
          filterType: ['text'],
          allowNone: true,
        },
        {
          key: 'meta_keywords_attribute',
          label: bcTranslations.mappingFields.labels.metaKeywords,
          type: 'attribute',
          filterType: ['text'],
          allowNone: true,
        },
        {
          key: 'page_title_attribute',
          label: bcTranslations.mappingFields.labels.pageTitle,
          type: 'attribute',
          filterType: ['text'],
          allowNone: true,
        },
      ],
    },
    {
      title: bcTranslations.mappingFields.sections.taxCompliance.title,
      description: bcTranslations.mappingFields.sections.taxCompliance.description,
      fields: [
        {
          key: 'tax_class_id_attribute',
          label: bcTranslations.mappingFields.labels.taxClassId,
          type: 'attribute',
          filterType: ['number', 'text'],
          description: bcTranslations.mappingFields.fields.taxClass,
          allowNone: true,
        },
      ],
    },
    {
      title: bcTranslations.mappingFields.sections.customFields.title,
      description: bcTranslations.mappingFields.sections.customFields.description,
      fields: [
        {
          key: 'custom_field_1_name',
          label: bcTranslations.mappingFields.labels.customField1Name,
          type: 'text',
          description: bcTranslations.mappingFields.fields.customFieldName,
          allowNone: true,
        },
        {
          key: 'custom_field_1_attribute',
          label: bcTranslations.mappingFields.labels.customField1Attribute,
          type: 'attribute',
          description: bcTranslations.mappingFields.fields.customField1,
          allowNone: true,
        },
        {
          key: 'custom_field_2_name',
          label: bcTranslations.mappingFields.labels.customField2Name,
          type: 'text',
          description: bcTranslations.mappingFields.fields.customFieldName,
          allowNone: true,
        },
        {
          key: 'custom_field_2_attribute',
          label: bcTranslations.mappingFields.labels.customField2Attribute,
          type: 'attribute',
          description: bcTranslations.mappingFields.fields.customField2,
          allowNone: true,
        },
        {
          key: 'custom_field_3_name',
          label: bcTranslations.mappingFields.labels.customField3Name,
          type: 'text',
          description: bcTranslations.mappingFields.fields.customFieldName,
          allowNone: true,
        },
        {
          key: 'custom_field_3_attribute',
          label: bcTranslations.mappingFields.labels.customField3Attribute,
          type: 'attribute',
          description: bcTranslations.mappingFields.fields.customField3,
          allowNone: true,
        },
      ],
    },
    {
      title: bcTranslations.mappingFields.sections.categories.title,
      description: bcTranslations.mappingFields.sections.categories.description,
      fields: [
        {
          key: 'auto_category_mapping_enabled',
          label: bcTranslations.mappingFields.labels.autoCategoryMappingEnabled,
          type: 'boolean',
          description: bcTranslations.mappingFields.fields.categoryMapping,
          allowDefault: true,
        },
      ],
    },
  ];
}

function SalesChannelMappingSection({ form, configId }: { form: any; configId: string | number }) {
  const bcTranslations = useBigCommerceTranslations();
  const [kernlogicChannels, setKernlogicChannels] = useState<SalesChannel[]>([]);
  const [bigcommerceChannels, setBigcommerceChannels] = useState<BigCommerceChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch KernLogic sales channels
  const fetchKernlogicChannels = useCallback(async () => {
    try {
      const response = await api.get('/api/channels/');
      const channelsData = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];
      setKernlogicChannels(channelsData);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.message || bcTranslations.errors.genericError;
      setError(`${bcTranslations.mapping.loadChannelsError}: ${errorMessage}`);
    }
  }, [bcTranslations.mapping.loadChannelsError, bcTranslations.errors.genericError]);

  // Fetch BigCommerce channels
  const fetchBigcommerceChannels = useCallback(async () => {
    if (!configId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(
        `/api/external-platform-configs/${configId}/bigcommerce-channels/`
      );
      // eslint-disable-next-line no-console
      console.debug('BigCommerce channels response', response.data);
      setBigcommerceChannels(response.data.channels || []);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.message || bcTranslations.errors.genericError;
      setError(`${bcTranslations.mapping.loadChannelsError}: ${errorMessage}`);
      toast({
        title: bcTranslations.status.error,
        description: `${bcTranslations.mapping.loadChannelsError}: ${errorMessage}`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [configId, bcTranslations]);

  useEffect(() => {
    fetchKernlogicChannels();
    if (configId) {
      fetchBigcommerceChannels();
    }
  }, [configId, fetchKernlogicChannels, fetchBigcommerceChannels]);

  const channelMappings = form.watch('settings.mapping.sales_channel_mappings') || {};

  const updateChannelMapping = (kernlogicChannelId: string, bigcommerceChannelId: string) => {
    const currentMappings = form.getValues('settings.mapping.sales_channel_mappings') || {};

    if (bigcommerceChannelId === '__none__') {
      // Remove mapping
      const newMappings = { ...currentMappings };
      delete newMappings[kernlogicChannelId];
      form.setValue('settings.mapping.sales_channel_mappings', newMappings);
    } else {
      // Add/update mapping
      form.setValue('settings.mapping.sales_channel_mappings', {
        ...currentMappings,
        [kernlogicChannelId]: parseInt(bigcommerceChannelId),
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          {bcTranslations.mapping.title}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchBigcommerceChannels}
            disabled={loading || !configId}
          >
            <RefreshCw className={`mr-2 size-4 ${loading ? 'animate-spin' : ''}`} />
            {bcTranslations.buttons.syncNow}
          </Button>
        </CardTitle>
        <p className="text-sm text-muted-foreground">{bcTranslations.mapping.description}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">{error}</div>
        )}

        {kernlogicChannels.length === 0 ? (
          <div className="rounded-md bg-muted p-3 text-sm">
            {bcTranslations.channelMapping.noKernlogicChannels}
          </div>
        ) : (
          kernlogicChannels.map(klChannel => (
            <div key={klChannel.id} className="grid grid-cols-2 items-center gap-4">
              <div>
                <Label className="font-medium">{klChannel.name}</Label>
                <p className="text-xs text-muted-foreground">
                  {bcTranslations.channelMapping.codeLabel}: {klChannel.code} |{' '}
                  {bcTranslations.channelMapping.statusLabel}:{' '}
                  {klChannel.is_active
                    ? bcTranslations.status.connected
                    : bcTranslations.status.disconnected}
                </p>
              </div>
              <div>
                <Select
                  value={channelMappings[klChannel.id]?.toString() || ''}
                  onValueChange={value => updateChannelMapping(klChannel.id.toString(), value)}
                  disabled={loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={bcTranslations.placeholders.selectChannel} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      {bcTranslations.placeholders.noChannelSelected}
                    </SelectItem>
                    {bigcommerceChannels.map(bcChannel => (
                      <SelectItem key={bcChannel.id} value={bcChannel.id.toString()}>
                        <div className="flex flex-col">
                          <span>{bcChannel.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {bcChannel.type} | {bcChannel.platform} | {bcChannel.status}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))
        )}

        {bigcommerceChannels.length === 0 && !loading && !error && (
          <div className="rounded-md bg-muted p-3 text-sm">
            {bcTranslations.mapping.noChannelsFound}
          </div>
        )}

        <div className="border-t pt-2 text-xs text-muted-foreground">
          <p>
            💡 <strong>{bcTranslations.channelMapping.tipTitle}</strong>{' '}
            {bcTranslations.tooltips.channelMapping}
          </p>
          <ul className="ml-4 mt-1 list-disc space-y-1">
            <li>{bcTranslations.channelMapping.benefits.syncSpecific}</li>
            <li>{bcTranslations.channelMapping.benefits.manageInventory}</li>
            <li>{bcTranslations.channelMapping.benefits.controlVisibility}</li>
          </ul>
          <p className="mt-2">
            <ExternalLink className="mr-1 inline size-3" />
            <a
              href="https://developer.bigcommerce.com/docs/rest-management/channels"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {bcTranslations.channelMapping.learnMore}
            </a>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export function BigCommerceMapping(props: BigCommerceMappingProps) {
  const bcTranslations = useBigCommerceTranslations();
  const configId =
    props.configId ??
    props.form.watch('id') ??
    props.form.getValues('id') ??
    props.form.getValues('configId');
  // eslint-disable-next-line no-console
  console.debug('BigCommerceMapping configId', configId);

  return (
    <div className="space-y-6">
      <BaseConnectorMapping {...props} sections={getBigCommerceMappingSections(bcTranslations)} />
      <SalesChannelMappingSection form={props.form} configId={configId} />
    </div>
  );
}
